import { atom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';

import { Toast } from '@common/overlay-manager';

import {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_shipping_fee as ShippingFee,
  PdpCatalogFulfillmentType,
} from '../graphql/select-option_types.2';
import {
  BogoInfo,
  CustomOptionsInfo,
  NotificationInfo,
  RestockNotificationInfo,
  SelectedItem,
  SelectedItemMap,
} from '../types/option-type';
import { initActiveIndex, initAdditionalOptions, initOptions, initTextOptions } from './utils/init';
import {
  ActiveIndexAtom,
  ActiveParentIndexAtom,
  AdditionalOptionsAtom,
  BogoInfoAtom,
  BogoRequiredQuantityAtom,
  displayDeliveryTypeToggleButtonAtom,
  FirstSoldoutIndexAtom,
  IsItemsShownAtom,
  IsProductTypeZigzinAtom,
  IsRestockNotiDrawerShownAtom,
  IsShowShippingDetailAtom,
  IsSingleSelectionAllowedAtom,
  ItemsAtom,
  MachingOptionAtom,
  MaximumCountAtom,
  OnlyZigzinOptionAtom,
  OptionsAtom,
  OptionSourceDataAtom,
  OriginOptionDataAtom,
  ProductIdAtom,
  ProductItemListAtom,
  SelectedOptionRestockNotiInfoAtom,
  TextOptionsAtom,
} from './baseAtoms';
import { genItemKey, initData, setIsZigzinOfValueList, updateOptionValueList } from './util';

export const resetAtoms = atom(
  null,
  (
    get,
    set,
    payload: {
      options_data: CustomOptionsInfo;
      shipping_fee: ShippingFee | null;
      bogo_info?: BogoInfo | null;
      data_saved?: boolean;
    },
  ) => {
    const { options_data, shipping_fee, data_saved, bogo_info } = payload;
    const bogoInfo = bogo_info ? bogo_info : (options_data.promotion_info?.bogo_info ?? null);
    const bogo_required_quantity = bogo_info ? bogo_info.required_quantity : (bogoInfo?.required_quantity ?? null);

    const only_zigzig_option = get(OnlyZigzinOptionAtom);

    const isProductTypeZigzin = options_data.fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN;
    const covertedIsZigzinOptionData = setIsZigzinOfValueList(options_data, shipping_fee);
    const covertOptionData = initData(covertedIsZigzinOptionData, only_zigzig_option);

    set(displayDeliveryTypeToggleButtonAtom, covertOptionData.is_only_zigzin_button_visible);
    set(BogoInfoAtom, bogoInfo);
    set(BogoRequiredQuantityAtom, bogo_required_quantity);

    set(OptionSourceDataAtom, covertedIsZigzinOptionData);
    set(OriginOptionDataAtom, covertOptionData);
    set(ProductIdAtom, covertOptionData.id);
    set(TextOptionsAtom, initTextOptions(covertOptionData, bogo_required_quantity));
    set(SelectedOptionRestockNotiInfoAtom, {} as RestockNotificationInfo);

    if (!data_saved) {
      set(OptionsAtom, initOptions(covertOptionData.product_option_list, bogo_required_quantity));
      set(ItemsAtom, {} as SelectedItemMap);
      set(ActiveIndexAtom, initActiveIndex(covertOptionData));
      set(ActiveParentIndexAtom, initActiveIndex(covertOptionData));
    } else {
      const options = get(OptionsAtom);
      const cloneOptions = cloneDeep(options);
      const updatedOption = updateOptionValueList(cloneOptions, covertOptionData);
      set(OptionsAtom, updatedOption);
    }

    set(IsItemsShownAtom, false);
    set(IsShowShippingDetailAtom, false);
    set(IsRestockNotiDrawerShownAtom, false);
    set(FirstSoldoutIndexAtom, -1);

    if (only_zigzig_option) {
      set(ProductItemListAtom, covertOptionData.zigzin_item_list);
    } else {
      set(ProductItemListAtom, covertOptionData.matched_item_list);
    }

    set(AdditionalOptionsAtom, initAdditionalOptions(covertOptionData));

    set(IsProductTypeZigzinAtom, isProductTypeZigzin);
    set(MachingOptionAtom, covertOptionData.matching_catalog_product_info?.option_list ?? null);
  },
);

export const GoToOptionsAtom = atom(
  null,
  (_get, set, { parent_key, child_key }: { parent_key: number; child_key: number }) => {
    set(ActiveParentIndexAtom, parent_key);
    set(ActiveIndexAtom, child_key);

    set(OptionsAtom, (prevOptions) => {
      const options = prevOptions.slice(0);

      options.forEach((nested_options, parent_index) => {
        nested_options.forEach((option, child_index) => {
          const is_next_option = (parent_index === parent_key && child_index >= child_key) || parent_index > parent_key;

          if (is_next_option) {
            option.selected_value = '';
          }
        });
      });

      return options;
    });
  },
);

export const AddItemAtom = atom(null, (get, set, selectedItem: SelectedItem) => {
  const itemKey = genItemKey(selectedItem);
  const maxCount = get(MaximumCountAtom);
  const isSingleSelectionAllowed = get(IsSingleSelectionAllowedAtom);

  set(ItemsAtom, (prevItems) => {
    const items = { ...prevItems };
    if (isSingleSelectionAllowed) {
      items[itemKey] = selectedItem;
    } else {
      if (items[itemKey]) {
        if (items[itemKey].count >= maxCount) {
          Toast.show('상품의 최대구매수량을 초과합니다.');
          return items;
        }

        Toast.show('동일한 옵션을 선택하여 수량을 추가합니다.');
        items[itemKey].count++;
      } else {
        items[itemKey] = selectedItem;
      }
    }

    return items;
  });

  set(IsItemsShownAtom, true);
});

export const ChangeItemCountsAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      item: SelectedItem;
      count: number;
    },
  ) => {
    const itemKey = genItemKey(payload.item);
    set(ItemsAtom, (prevItems) => {
      const items = { ...prevItems };
      items[itemKey] = { ...payload.item, count: payload.count };
      return items;
    });
  },
);

export const RemoveItemAtom = atom(null, (get, set, item: SelectedItem) => {
  const items = get(ItemsAtom);
  const textOptions = get(TextOptionsAtom);

  if (Object.keys(items).length === 1 && items[genItemKey(item)]) {
    const has_required_text_option = flatten(items[genItemKey(item)].text_options).some((o) => o.is_required);
    const reset_index = has_required_text_option || !!textOptions[0]?.find((o) => o.is_required) ? -1 : 0;

    set(GoToOptionsAtom, {
      parent_key: reset_index,
      child_key: reset_index,
    });
    set(IsItemsShownAtom, false);
  }
  set(ItemsAtom, (prevItems) => {
    const items = { ...prevItems };
    delete items[genItemKey(item)];
    return items;
  });
});

export const RemoveAllItemsAtom = atom(null, (get, set) => {
  set(ItemsAtom, {});
});

export const ActivateRestockNotiDrawerAtom = atom(null, (_get, set, payload: { active: boolean; index?: number }) => {
  set(IsRestockNotiDrawerShownAtom, payload.active);
  set(FirstSoldoutIndexAtom, payload.index ?? -1);

  if (!payload.active) {
    set(ActiveIndexAtom, -1);
    set(ActiveParentIndexAtom, -1);
  }
});

export const RequestRestockNotiAtom = atom(null, (get, set, payload: NotificationInfo) => {
  set(ProductItemListAtom, (prevItemList) => {
    const itemList = prevItemList.slice(0);
    itemList.map((item) => {
      if (item.id === payload.product_item_id) {
        item.wms_notification_info = { active: payload.active };
      }
      return item;
    });
    return itemList;
  });
  set(SelectedOptionRestockNotiInfoAtom, payload);
});

export const ResetOptionsAtom = atom(null, (get, set) => {
  const bogo_required_quantity = get(BogoRequiredQuantityAtom);
  const originOptionsData = get(OriginOptionDataAtom);

  set(OptionsAtom, originOptionsData ? initOptions(originOptionsData.product_option_list, bogo_required_quantity) : []);
  set(TextOptionsAtom, originOptionsData ? initTextOptions(originOptionsData, bogo_required_quantity) : []);
});

export const resetSelectedOptionAtoms = atom(null, (get, set) => {
  const originOptionData = get(OriginOptionDataAtom);
  const bogoRequiredQuantity = get(BogoRequiredQuantityAtom);

  if (originOptionData?.product_option_list) {
    set(OptionsAtom, initOptions(originOptionData.product_option_list, bogoRequiredQuantity));
  }
});

export const ActivateOptionAtom = atom(null, (get, set) => {
  const options = get(OptionsAtom);
  const textOptions = get(TextOptionsAtom);
  const active_parent_index = get(ActiveParentIndexAtom);
  const isAllOptional = !textOptions[active_parent_index].find(({ is_required }) => is_required);
  let isReadyToActivate = !textOptions[active_parent_index].find(
    ({ is_required, selected_value }) => is_required && !selected_value,
  );
  if (isAllOptional) {
    const beingSelectedOption = !!options[active_parent_index].find(({ selected_value }) => selected_value);
    isReadyToActivate =
      !textOptions[active_parent_index].find(({ selected_value }) => !selected_value) || beingSelectedOption;
  }

  set(ActiveIndexAtom, (prevIndex) => {
    if (isReadyToActivate) {
      return options[active_parent_index].findIndex(({ selected_value }) => !selected_value);
    } else {
      return prevIndex - 1;
    }
  });
});

export const EnterOptionAtom = atom(
  null,
  (
    get,
    set,
    payload: {
      name: string;
      value: string;
    },
  ) => {
    set(TextOptionsAtom, (prevTextOptions) => {
      const textOptions = prevTextOptions.map((arr) => cloneDeep(arr));
      const active_parent_index = get(ActiveParentIndexAtom);
      const extraOption = textOptions[active_parent_index]?.find(({ name }) => name === payload.name);

      if (extraOption) {
        extraOption.selected_value = payload.value;
      }

      return textOptions;
    });
  },
);

export const SelectOptionAtom = atom(
  null,
  (get, set, payload: { name: string; id: string; value: string; value_id: string; is_zigzin: boolean }) => {
    set(OptionsAtom, (prevOptions) => {
      const active_parent_index = get(ActiveParentIndexAtom);
      const options = prevOptions.map((arr) => cloneDeep(arr));
      const current_option_items = options[active_parent_index];
      const target_option = current_option_items.find(({ id, name }) => {
        if (payload.id) {
          return id === payload.id;
        }
        return name === payload.name;
      });

      if (target_option) {
        target_option.selected_value_id = payload.value_id;
        target_option.selected_value = payload.value;
        target_option.is_zigzin = payload.is_zigzin;
      }

      return options;
    });
  },
);
